import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEdit, faHistory, faCheckCircle, faTimesCircle, faInfinity } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import baseURL from '../../url';
import NewCodigo from '../NewCodigo/NewCodigo';
import moneda from '../../moneda';
import './CodigosData.css';

export default function CodigosData() {
    const [codigos, setCodigos] = useState([]);
    const [cuponEditar, setCuponEditar] = useState(null);
    const [modalHistorialOpen, setModalHistorialOpen] = useState(false);
    const [historialData, setHistorialData] = useState(null);

    useEffect(() => {
        cargarCodigos();
    }, []);

    const cargarCodigos = () => {
        fetch(`${baseURL}/codigosGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    setCodigos(data.codigos || []);
                } else {
                    setCodigos([]);
                }
            })
            .catch(error => console.error('Error al cargar códigos:', error));
    };

    const eliminarCodigo = (idCodigo, codigo) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: `¿Deseas eliminar el cupón "${codigo}"? Esta acción no se puede revertir.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(`${baseURL}/codigosDelete.php?idCodigo=${idCodigo}`, {
                    method: 'DELETE',
                })
                    .then(response => response.json())
                    .then(data => {
                        if (data.mensaje) {
                            Swal.fire(
                                '¡Eliminado!',
                                data.mensaje,
                                'success'
                            );
                            cargarCodigos();
                        } else if (data.error) {
                            toast.error(data.error);
                        }
                    })
                    .catch(error => {
                        console.error('Error al eliminar código:', error);
                        toast.error('Error al eliminar cupón');
                    });
            }
        });
    };

    const editarCodigo = (idCodigo) => {
        setCuponEditar(idCodigo);
    };

    const verHistorial = async (idCodigo) => {
        try {
            const response = await fetch(`${baseURL}/codigosHistorial.php?idCodigo=${idCodigo}`);
            const data = await response.json();
            
            if (data.success) {
                setHistorialData(data);
                setModalHistorialOpen(true);
            } else {
                toast.error(data.error || 'Error al cargar historial');
            }
        } catch (error) {
            console.error('Error al cargar historial:', error);
            toast.error('Error al cargar historial');
        }
    };

    const cerrarModalHistorial = () => {
        setModalHistorialOpen(false);
        setHistorialData(null);
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return '-';
        const date = new Date(fecha);
        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatearPrecio = (precio) => {
        return `${moneda} ${parseFloat(precio).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
    };

    return (
        <div className='codigos-data-container'>
            <ToastContainer />
            
            <NewCodigo 
                cuponEditar={cuponEditar}
                onCerrar={() => setCuponEditar(null)}
                onActualizar={() => {
                    cargarCodigos();
                    setCuponEditar(null);
                }}
            />

            <div className='table-container'>
                <table className='table cupones-table'>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Descripción</th>
                            <th>Descuento</th>
                            <th>Aplicable a</th>
                            <th>Usos</th>
                            <th>Estado</th>
                            <th>Vigencia</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {codigos.length === 0 ? (
                            <tr>
                                <td colSpan='8' style={{ textAlign: 'center', padding: '20px' }}>
                                    No hay cupones registrados
                                </td>
                            </tr>
                        ) : (
                            codigos.map(item => (
                                <tr key={item.idCodigo}>
                                    <td>
                                        <span className='codigo-badge'>{item.codigo}</span>
                                    </td>
                                    <td>{item.descripcion}</td>
                                    <td>
                                        <span className='descuento-value'>
                                            {item.tipo_descuento === 'porcentaje' 
                                                ? `${item.valor_descuento}%`
                                                : formatearPrecio(item.valor_descuento)
                                            }
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`aplicar-badge aplicar-${item.aplicar_a}`}>
                                            {item.aplicar_a === 'todos' && 'Todos'}
                                            {item.aplicar_a === 'categorias' && 'Categorías'}
                                            {item.aplicar_a === 'productos' && 'Productos'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className='usos-info'>
                                            {item.usos_actuales}
                                            {item.limite_usos ? ` / ${item.limite_usos}` : ' / ∞'}
                                        </span>
                                    </td>
                                    <td>
                                        {item.estado === 'activo' ? (
                                            <span className='estado-badge estado-activo'>
                                                <FontAwesomeIcon icon={faCheckCircle} /> Activo
                                            </span>
                                        ) : (
                                            <span className='estado-badge estado-inactivo'>
                                                <FontAwesomeIcon icon={faTimesCircle} /> Inactivo
                                            </span>
                                        )}
                                        {item.expirado && (
                                            <span className='estado-badge estado-expirado'>
                                                Expirado
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {item.fecha_fin ? (
                                            <span className='fecha-vigencia'>
                                                {formatearFecha(item.fecha_fin)}
                                            </span>
                                        ) : (
                                            <span className='fecha-vigencia'>
                                                <FontAwesomeIcon icon={faInfinity} /> Sin límite
                                            </span>
                                        )}
                                    </td>
                                    <td className='acciones-cell'>
                                        <button 
                                            className='btn-accion btn-historial' 
                                            onClick={() => verHistorial(item.idCodigo)}
                                            title='Ver historial'
                                        >
                                            <FontAwesomeIcon icon={faHistory} />
                                        </button>
                                        <button 
                                            className='btn-accion btn-editar' 
                                            onClick={() => editarCodigo(item.idCodigo)}
                                            title='Editar'
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button 
                                            className='btn-accion btn-eliminar' 
                                            onClick={() => eliminarCodigo(item.idCodigo, item.codigo)}
                                            title='Eliminar'
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal de Historial */}
            {modalHistorialOpen && historialData && (
                <div className='modal-overlay' onClick={cerrarModalHistorial}>
                    <div className='modal-historial' onClick={(e) => e.stopPropagation()}>
                        <div className='modal-header'>
                            <h2>Historial del Cupón</h2>
                            <button className='btn-close' onClick={cerrarModalHistorial}>&times;</button>
                        </div>
                        
                        <div className='modal-body'>
                            <div className='historial-info'>
                                <div className='info-item'>
                                    <strong>Código:</strong> {historialData.cupon.codigo}
                                </div>
                                <div className='info-item'>
                                    <strong>Descripción:</strong> {historialData.cupon.descripcion}
                                </div>
                                <div className='info-item'>
                                    <strong>Usos totales:</strong> {historialData.cupon.usos_actuales}
                                    {historialData.cupon.limite_usos 
                                        ? ` / ${historialData.cupon.limite_usos}`
                                        : ' / Ilimitado'
                                    }
                                </div>
                            </div>

                            {historialData.historial.length === 0 ? (
                                <div className='no-historial'>
                                    <p>Este cupón aún no ha sido utilizado</p>
                                </div>
                            ) : (
                                <div className='table-container'>
                                    <table className='table historial-table'>
                                        <thead>
                                            <tr>
                                                <th>Pedido</th>
                                                <th>Cliente</th>
                                                <th>Total Pedido</th>
                                                <th>Descuento</th>
                                                <th>Estado</th>
                                                <th>Fecha de Uso</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historialData.historial.map((item) => (
                                                <tr key={item.id}>
                                                    <td>#{item.pedido_id}</td>
                                                    <td>{item.cliente_nombre || '-'}</td>
                                                    <td>{formatearPrecio(item.total_pedido)}</td>
                                                    <td className='descuento-historial'>
                                                        -{formatearPrecio(item.monto_descuento)}
                                                    </td>
                                                    <td>
                                                        <span className={`estado-pedido estado-${item.estado_pedido?.toLowerCase()}`}>
                                                            {item.estado_pedido || '-'}
                                                        </span>
                                                    </td>
                                                    <td>{formatearFecha(item.fecha_uso)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
